import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePagedQuery } from "./use-paged-query";
import { api } from "./api-client";

export type RunStatus = "PENDING" | "RUNNING" | "PAUSED" | "COMPLETED" | "CANCELLED" | "FAILED";

export type TaskStatus =
    | "PENDING"
    | "QUEUED"
    | "SENDING"
    | "SENT"
    | "FAILED"
    | "UNCERTAIN"
    | "SKIPPED"
    | "CANCELLED";

export type CampaignRun = {
    id: string;
    campaignId: string;
    campaignName: string;
    accountName: string;
    status: RunStatus;
    triggeredBy: string;
    totalCount: number;
    sentCount: number;
    failedCount: number;
    uncertainCount: number;
    skippedCount: number;
    startedAt: string | null;
    finishedAt: string | null;
    blockedReason: string | null;
    createdAt: string;
};

export type SendTask = {
    id: string;
    groupNameSnapshot: string;
    status: TaskStatus;
    attempt: number;
    zaloMsgId: string | null;
    verifiedBy: string | null;
    errorKind: string | null;
    errorMessage: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    durationMs: number | null;
};

export function useHistory(accountId?: string) {
    return usePagedQuery<CampaignRun>({
        queryKey: ["history", accountId],
        path: `/api/history${accountId ? `?accountId=${accountId}` : ""}`,
    });
}

export function useRunTasks(runId: string, status?: string) {
    return usePagedQuery<SendTask>({
        queryKey: ["run-tasks", runId, status ?? ""],
        path: `/api/runs/${runId}/tasks${status ? `?status=${status}` : ""}`,
        refetchInterval: 4_000,
    });
}

/** Theo dõi tiến độ theo thời gian thực qua SSE, tự dừng khi lượt gửi kết thúc. */
export function useRunProgress(runId: string | undefined) {
    const [run, setRun] = useState<CampaignRun | null>(null);

    useEffect(() => {
        if (!runId) return;

        const source = new EventSource(`/api/runs/${runId}/stream`);
        source.onmessage = (message) => setRun(JSON.parse(message.data as string) as CampaignRun);
        source.onerror = () => source.close();

        return () => source.close();
    }, [runId]);

    return run;
}

function useRunMutation<TVariables>(fn: (variables: TVariables) => Promise<unknown>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["run-tasks"] });
            void queryClient.invalidateQueries({ queryKey: ["history"] });
            void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        },
    });
}

export function useRunAction() {
    return useRunMutation(({ runId, action }: { runId: string; action: "pause" | "resume" | "cancel" | "retry-failed" }) =>
        api.post(`/api/runs/${runId}/${action}`),
    );
}

export function useTaskAction() {
    return useRunMutation(({ taskId, action }: { taskId: string; action: "verify" | "mark-sent" | "resend" }) =>
        api.post(`/api/tasks/${taskId}/${action}`),
    );
}
